// In-pane search (#4401, epic #4391): Ctrl+F over ONE pane's rendered lines.
// Pure — the pane owns the query, the current match and the scrolling; this
// answers "which rows match" and "which is next".
//
// FIND, NOT FILTER. The filter box narrows what a pane shows; search leaves
// every line in place and steps between the ones that contain the text. It
// matches the SAME rendered text the bare-word filter does (`rowSearchText`),
// as one literal, case-insensitive substring — `two words` finds that phrase,
// not rows holding each word somewhere.
//
// DEPTH. Search reads what the pane holds: the shared ring buffer, capped at
// `TERMINAL_BUFFER_CAPACITY` (5,000) rows, narrowed by the pane's filter. The
// pane states the oldest row it searched ("searched back to …") instead of
// implying it looked at everything the server has.

import type { TerminalEvent } from '@developerz/domain';
import { rowSearchText } from './filter';

/** Ids of the rows containing `needle`, in the rows' own (oldest-first) order. */
export function findMatches(rows: readonly TerminalEvent[], needle: string): readonly string[] {
  const term = needle.trim().toLowerCase();
  if (term === '') return [];
  return rows.filter((row) => rowSearchText(row).includes(term)).map((row) => row.id);
}

/**
 * The match to show after stepping `direction` from `current`, wrapping at both
 * ends. With no current match, forward lands on the NEWEST match (the bottom,
 * where a terminal's eye already is) and backward on the one before it.
 */
export function stepMatch(current: number | null, total: number, direction: 1 | -1): number | null {
  if (total === 0) return null;
  if (current === null) return direction === 1 ? total - 1 : Math.max(0, total - 2);
  return (current + direction + total) % total;
}
